import { updateDialectAction } from "@/actions/user.actions";
import { EventNamesEnum } from "@/constants/constants";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useCulture } from "@/hooks/useCulture";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { CultureSelectionItemTv } from "@/screens/profile/components/CultureSelectionItemTv";
import { setFocusOnTv } from "@/utils/client.helpers";
import { isTvEnv } from "@/utils/common.helpers";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader } from "../Loader";
import { Column, Row } from "../Stack";
import { useUserContext } from "@/context/UserProvider";

interface ContentCultureSelectionProps {
  dialect: string;
  variant?: "light" | "default";
}

export const ContentCultureSelection = ({
  dialect,
  variant = "light",
}: ContentCultureSelectionProps) => {
  const { t } = useClientTranslations();

  return (
    <Column gap={2} useGrid fullWidth justify="stretch">
      <Row gap={1} fullWidth align="center" gapMd={2}>
        <p
          className={`text-sm font-bold md:text-[1.125rem] md:text-stg-grey-5 ${isTvEnv() ? "!text-2xl" : ""}`}
        >
          {t("user_culture_screen.content_culture.heading")}
        </p>
      </Row>

      <DialectSwitch dialect={dialect} />
    </Column>
  );
};

const INITIAL_VISIBLE_COUNT = 4;

const DialectSwitch = ({ dialect }: { dialect: string }) => {
  const router = useRouter();
  const { updateUserDialect } = useUserContext();
  const { enabledCultures } = useCulture();
  const { currentLanguage, t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const [loadingDialect, setLoadingDialect] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setFocusOnTv(`dialect-${dialect}`);
  }, []);

  // Reset loading state when dialect changes (after navigation)
  useEffect(() => {
    if (loadingDialect && dialect === loadingDialect) {
      setLoadingDialect(null);
    }
  }, [dialect, loadingDialect]);

  const dialectChangeHandler = async (selectedDialect: string) => {
    if (dialect !== selectedDialect && !loadingDialect) {
      setLoadingDialect(selectedDialect);
      try {
        trackEvent({
          eventName: EventNamesEnum.CONTENT_CULTURE_UPDATED,
          properties: {
            previous_content_culture: dialect,
            updated_content_culture: selectedDialect,
            updated_from: "settings_page",
          },
        });

        // Fire and forget - don't await to prevent blocking navigation
        updateUserDialect(selectedDialect).catch((error) => {
          console.error("Error updating user dialect:", error);
        });
        updateDialectAction({
          dialect: selectedDialect,
          shouldRedirect: false,
        }).catch((error) => {
          console.error("Error updating dialect action:", error);
        });
      } catch (error) {
        console.error("Error in dialect change handler:", error);
      }

      // Always navigate, even if the above operations fail
      router.replace(`/${currentLanguage}/${selectedDialect}`);
    }
  };

  // Ensure selected dialect is always visible in collapsed state
  const getVisibleCultures = () => {
    if (isExpanded || enabledCultures.length <= INITIAL_VISIBLE_COUNT) {
      return enabledCultures;
    }

    const selectedIndex = enabledCultures.findIndex(
      (c) => c.name.toLocaleLowerCase() === dialect,
    );

    // If selected is within initial visible, return first N items
    if (selectedIndex < INITIAL_VISIBLE_COUNT) {
      return enabledCultures.slice(0, INITIAL_VISIBLE_COUNT);
    }

    // Otherwise, include selected in visible items by swapping
    const visible = enabledCultures.slice(0, INITIAL_VISIBLE_COUNT - 1);
    visible.push(enabledCultures[selectedIndex]);
    return visible;
  };

  const visibleCultures = getVisibleCultures();
  const hasMoreCultures = enabledCultures.length > INITIAL_VISIBLE_COUNT;

  return (
    <>
      {loadingDialect &&
        typeof window !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-[50] h-full w-full bg-black/50">
            <Loader />
          </div>,
          document.body,
        )}
      {isTvEnv() ? (
        <Row fullWidth justify="stretch" gap={4} useGrid>
          {enabledCultures.map((_culture) => (
            <CultureSelectionItemTv
              key={_culture.abbreviation}
              culture={_culture}
              isSelected={dialect === _culture.name.toLocaleLowerCase()}
              onClick={() =>
                dialectChangeHandler(_culture.name.toLocaleLowerCase())
              }
              isDisabled={!!loadingDialect}
            />
          ))}
        </Row>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {visibleCultures.map((_culture) => (
              <DialectOption
                label={_culture.title}
                key={_culture.abbreviation}
                dialect={_culture.name.toLocaleLowerCase()}
                selectedDialect={dialect}
                dialectChangeHandler={dialectChangeHandler}
                isDisabled={!!loadingDialect}
              />
            ))}
          </div>
          {hasMoreCultures && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm font-medium text-stg-grey-4 hover:text-stg-grey-5"
            >
              {isExpanded
                ? t("common.viewLess") || "View Less"
                : t("common.viewMore") ||
                  `View More (${enabledCultures.length - INITIAL_VISIBLE_COUNT})`}
            </button>
          )}
        </div>
      )}
    </>
  );
};

export const DialectOption = ({
  label,
  dialect,
  selectedDialect,
  dialectChangeHandler,
  isDisabled = false,
}: {
  label: string;
  selectedDialect: string;
  dialect: string;
  dialectChangeHandler: Function;
  isDisabled?: boolean;
}) => {
  return (
    <button
      onClick={async () => {
        await dialectChangeHandler(dialect);
      }}
      disabled={isDisabled}
      className={`flex h-10 items-center justify-center rounded-lg border-2 text-sm font-medium transition-colors ${isDisabled ? "cursor-not-allowed opacity-50" : ""} ${selectedDialect === dialect ? "border-stg-grey-5 bg-stg-grey-3 text-white" : "border-stg-grey-3 bg-stg-grey-1 text-stg-grey-4 hover:border-stg-grey-4 hover:bg-stg-grey-2"}`}
      data-testid={`${selectedDialect === dialect ? "selected-dialect-button" : ""}`}
    >
      {label}
    </button>
  );
};
